import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, CheckCircle, Trophy, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const TransactionValidator = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [gameStarted, setGameStarted] = useState(false);
  const [currentTx, setCurrentTx] = useState(0);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const transactions = [
    {
      from: '0xABC123',
      to: '0xDEF456',
      amount: 5,
      balance: 10,
      signature: 'valid',
      gasPrice: 50,
      isValid: true,
      reason: 'All checks pass: sufficient balance, valid signature, adequate gas.'
    },
    {
      from: '0xGHI789',
      to: '0xJKL012',
      amount: 100,
      balance: 50,
      signature: 'valid',
      gasPrice: 50,
      isValid: false,
      reason: 'Insufficient balance: trying to send 100 but only has 50.'
    },
    {
      from: '0xMNO345',
      to: '0xPQR678',
      amount: 10,
      balance: 20,
      signature: 'invalid',
      gasPrice: 50,
      isValid: false,
      reason: 'Invalid signature: transaction not properly signed by sender.'
    },
    {
      from: '0xSTU901',
      to: '0xVWX234',
      amount: 3,
      balance: 10,
      signature: 'valid',
      gasPrice: 0,
      isValid: false,
      reason: 'Insufficient gas: gas price is 0, transaction will not be processed.'
    },
    {
      from: '0xYZA567',
      to: '0xBCD890',
      amount: 15,
      balance: 20,
      signature: 'valid',
      gasPrice: 100,
      isValid: true,
      reason: 'All checks pass: sufficient balance (20 > 15), valid signature, adequate gas.'
    }
  ];

  const handleValidation = (isValid) => {
    const tx = transactions[currentTx];
    if (tx.isValid === isValid) {
      setScore(score + 300);
      toast.success('Correct validation! +300 points');
    } else {
      toast.error('Incorrect validation!');
    }

    setTimeout(() => {
      if (currentTx < transactions.length - 1) {
        setCurrentTx(currentTx + 1);
      } else {
        endGame();
      }
    }, 3000);
  };

  const endGame = async () => {
    setGameOver(true);
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'validator',
        score: score,
        time_taken: 0
      });
      toast.success(`Validation complete! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Transaction Validator</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Review transactions and determine if they're valid or invalid. Check balance, signatures, and gas fees. Each correct validation earns 300 points!
            </p>
            <Button
              onClick={() => setGameStarted(true)}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Validating
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Validation Complete!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Total Score</div>
              <div className="mt-4 text-zinc-400">
                Correct: <span className="text-primary font-mono">{score / 300}/{transactions.length}</span>
              </div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Validate Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const tx = transactions[currentTx];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Validator
        </Button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-grotesk font-bold text-white mb-1">Transaction Validator</h1>
            <p className="text-zinc-400 text-sm">Transaction {currentTx + 1} of {transactions.length}</p>
          </div>
          <div className="text-2xl font-mono font-bold text-primary" data-testid="game-score">{score}</div>
        </div>

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-8 mb-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-6">Transaction Details</h3>
          
          <div className="space-y-4 mb-8">
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <span className="text-zinc-500 text-sm">From</span>
              <span className="font-mono text-white" data-testid="tx-from">{tx.from}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <span className="text-zinc-500 text-sm">To</span>
              <span className="font-mono text-white" data-testid="tx-to">{tx.to}</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <span className="text-zinc-500 text-sm">Amount</span>
              <span className="font-mono text-primary font-bold" data-testid="tx-amount">{tx.amount} ETH</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <span className="text-zinc-500 text-sm">Sender Balance</span>
              <span className="font-mono text-white" data-testid="tx-balance">{tx.balance} ETH</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/5">
              <span className="text-zinc-500 text-sm">Signature</span>
              <span className={`font-mono font-semibold ${tx.signature === 'valid' ? 'text-primary' : 'text-red-500'}`} data-testid="tx-signature">
                {tx.signature.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-zinc-500 text-sm">Gas Price</span>
              <span className="font-mono text-white" data-testid="tx-gas">{tx.gasPrice} Gwei</span>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-4">Is this transaction valid?</h3>
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => handleValidation(true)}
                data-testid="validate-button"
                className="bg-primary/20 border-2 border-primary text-primary hover:bg-primary hover:text-black uppercase tracking-wide px-8 py-6 text-base h-auto"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Valid
              </Button>
              <Button
                onClick={() => handleValidation(false)}
                data-testid="reject-button"
                className="bg-red-500/20 border-2 border-red-500 text-red-400 hover:bg-red-500 hover:text-white uppercase tracking-wide px-8 py-6 text-base h-auto"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Invalid
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Validation Checklist</h3>
          <div className="space-y-2 text-sm text-zinc-400">
            <div>• Does sender have sufficient balance?</div>
            <div>• Is the signature valid?</div>
            <div>• Is the gas price adequate?</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionValidator;
